import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemeAccent = 'default' | 'rose' | 'blue' | 'green' | 'amber' | 'violet';

export const ACCENT_LABELS: Record<ThemeAccent, string> = {
  default: 'Neutral',
  rose: 'Rose',
  blue: 'Blue',
  green: 'Green',
  amber: 'Amber',
  violet: 'Violet',
};

export const ACCENT_SWATCHES: Record<ThemeAccent, string> = {
  default: 'oklch(0.488 0.243 264.376)',
  rose: 'oklch(0.645 0.246 16.439)',
  blue: 'oklch(0.546 0.215 262.881)',
  green: 'oklch(0.629 0.179 152.708)',
  amber: 'oklch(0.769 0.188 70.08)',
  violet: 'oklch(0.541 0.281 293.609)',
};

interface ThemeState {
  mode: ThemeMode;
  accent: ThemeAccent;
  setMode: (m: ThemeMode) => void;
  setAccent: (a: ThemeAccent) => void;
  toggleMode: () => void;
}

const STORAGE_KEY = 'admin-theme';
const DEFAULT_MODE: ThemeMode = 'dark';
const DEFAULT_ACCENT: ThemeAccent = 'default';

interface PersistedTheme {
  mode: ThemeMode;
  accent: ThemeAccent;
}

function readPersisted(): PersistedTheme {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mode: DEFAULT_MODE, accent: DEFAULT_ACCENT };
    const parsed = JSON.parse(raw) as Partial<PersistedTheme>;
    const mode: ThemeMode = parsed.mode === 'light' ? 'light' : 'dark';
    const accent: ThemeAccent =
      parsed.accent && parsed.accent in ACCENT_LABELS ? parsed.accent : DEFAULT_ACCENT;
    return { mode, accent };
  } catch {
    return { mode: DEFAULT_MODE, accent: DEFAULT_ACCENT };
  }
}

function applyToHtml({ mode, accent }: PersistedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  for (const a of Object.keys(ACCENT_LABELS) as ThemeAccent[]) {
    root.classList.toggle(`theme-${a}`, a === accent && a !== 'default');
  }
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readPersisted().mode);
  const [accent, setAccentState] = useState<ThemeAccent>(() => readPersisted().accent);

  useEffect(() => {
    applyToHtml({ mode, accent });
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, accent }));
    } catch {
      // localStorage unavailable — silently ignore
    }
  }, [mode, accent]);

  const setMode = useCallback((m: ThemeMode) => setModeState(m), []);
  const setAccent = useCallback((a: ThemeAccent) => setAccentState(a), []);
  const toggleMode = useCallback(() => setModeState((m) => (m === 'dark' ? 'light' : 'dark')), []);

  const value = useMemo<ThemeState>(
    () => ({ mode, accent, setMode, setAccent, toggleMode }),
    [mode, accent, setMode, setAccent, toggleMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
